import axios from 'axios';

/**
 * NFL intelligence product API. All endpoints are public (no entitlement
 * redaction, unlike MMA prediction payloads), so no auth interceptor is
 * attached. Product payloads are cached by backend Redis. The validation
 * status helper only shares one in-flight request per page load.
 */
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:9000/',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

let validationStatusRequest = null;

export function resetNflValidationStatusCache() {
  validationStatusRequest = null;
}

export default {
  /**
   * Public 2026 LIVE VALIDATION aggregate only.
   * GET /api/sports/nfl/validation-status
   * Shared in-flight promise so /picks can mount two notes without a second call.
   */
  getValidationStatus() {
    if (!validationStatusRequest) {
      validationStatusRequest = apiClient.get('/api/sports/nfl/validation-status');
    }
    return validationStatusRequest;
  },

  /** NFL home aggregate: current week, live/upcoming/recent games, PR preview, highlights. */
  getOverview() {
    return apiClient.get('/api/sports/nfl/overview');
  },

  /** Season index: { seasonCounts: { season -> gameCount } }. */
  getSeasons() {
    return apiClient.get('/api/sports/nfl/games/seasons');
  },

  /** Game archive; seasonType: 1 pre, 2 regular, 3 post. Returns weekCounts + games. */
  getGames({ season = null, seasonType = null, week = null } = {}) {
    const params = new URLSearchParams();
    if (season != null) params.set('season', season);
    if (seasonType != null) params.set('seasonType', seasonType);
    if (week != null) params.set('week', week);
    const qs = params.toString();
    return apiClient.get(`/api/sports/nfl/games${qs ? `?${qs}` : ''}`);
  },

  /** Game Center aggregate: state-aware pre/live/final payload. */
  getGameCenter(gameId) {
    return apiClient.get(`/api/sports/nfl/games/${gameId}`);
  },

  /**
   * Weekly intelligence document (stars, heating, matchup, movers, MVP, leaders).
   * GET /api/sports/nfl/week-intel
   * Optional params: season, week, seasonType. Omit all three to use the
   * backend default, which localhost verified as the same season / week /
   * seasonType as GET /api/sports/nfl/overview (currentWeek), not a completed-week cursor.
   */
  getWeekIntel({ season = null, week = null, seasonType = null } = {}) {
    const params = new URLSearchParams();
    if (season != null) params.set('season', season);
    if (week != null) params.set('week', week);
    if (seasonType != null) params.set('seasonType', seasonType);
    const qs = params.toString();
    return apiClient.get(`/api/sports/nfl/week-intel${qs ? `?${qs}` : ''}`);
  },

  /** Play-by-play + derived drives + provider WP series/turning points. */
  getPlays(gameId) {
    return apiClient.get(`/api/sports/nfl/games/${gameId}/plays`);
  },

  /** Team profile (season optional; defaults to backend current season). */
  getTeam(teamId, { season = null } = {}) {
    const qs = season != null ? `?season=${season}` : '';
    return apiClient.get(`/api/sports/nfl/teams/${teamId}${qs}`);
  },

  /** Player profile with position-group modules. */
  getPlayer(playerId, { season = null } = {}) {
    const qs = season != null ? `?season=${season}` : '';
    return apiClient.get(`/api/sports/nfl/players/${playerId}${qs}`);
  },

  /** Standings grouped AFC/NFC → divisions → rows. */
  getStandings({ season = null } = {}) {
    const qs = season != null ? `?season=${season}` : '';
    return apiClient.get(`/api/sports/nfl/standings${qs}`);
  },

  /** Current-state league injury report. */
  getInjuries() {
    return apiClient.get('/api/sports/nfl/injuries');
  },

  /** BarbershopTalk Power Rankings snapshot (week optional). */
  getPowerRankings({ season = null, week = null } = {}) {
    const params = new URLSearchParams();
    if (season != null) params.set('season', season);
    if (week != null) params.set('week', week);
    const qs = params.toString();
    return apiClient.get(`/api/sports/nfl/power-rankings${qs ? `?${qs}` : ''}`);
  },
};
